/*
 * HANDING A LINK TO SOMEBODY OUTSIDE THE APP (docs/5 § D260805-1222).
 *
 * There are exactly two ways a device can do this and both are feature-detected,
 * because on one platform neither is true. `InviteSheet` established the pattern
 * for an invitation and carries the long argument; this is the same mechanism
 * lifted out so an open letter and an invitation cannot drift apart about what
 * sharing means.
 *
 * NO PRE-WRITTEN MESSAGE, EVER. `navigator.share` is called with a `url` and
 * nothing else: a "check out this letter on Dove!" is marketing copy in somebody
 * else's voice, and the share sheet the OS opens already lets them type what they
 * actually mean.
 *
 * WHAT IS DELIBERATELY NOT HERE: any counter. Nothing records that a letter was
 * shared, or how often, or by whom — that is a reach metric with a growth curve
 * attached (docs/5 § D260801-1119), and the fact that the client cannot report it is
 * what keeps it from existing.
 */

/**
 * How long a control says *Copied*.
 *
 * Long enough to read, short enough that the control is back to saying what it does
 * before anybody looks again. It lived in `InviteSheet` and is here now because a
 * second control copies a link: two durations for one piece of feedback is two
 * things to notice have drifted.
 */
export const COPIED_MS = 1600;

/** The address of the conversation a letter belongs to, as a path. */
export function letterPath(id: string): string {
  return `/letters/${id}`;
}

/**
 * A letter's full URL, or `null` when the app does not know its own web origin.
 *
 * THE ORIGIN COMES FROM THE SERVER (`GET /me`), which is the whole subtlety: under
 * Capacitor `window.location.origin` is `capacitor://localhost`, so a link this
 * client composed for itself would be one nobody on earth can open — the same
 * reason an invitation's URL is built by the Worker (`lib/origin` on the API side).
 *
 * The browser's own origin is the FALLBACK rather than the source, so a bundle
 * talking to an older API still shares a working link on the web, and a native
 * shell that somehow has no origin shares nothing rather than nonsense.
 */
export function letterUrl(id: string, webOrigin: string | null): string | null {
  const origin =
    webOrigin ??
    (typeof window !== 'undefined' && window.location.origin.startsWith('http')
      ? window.location.origin
      : null);
  return origin ? `${origin.replace(/\/+$/, '')}${letterPath(id)}` : null;
}

/** What a device can do with a link. Both are absent on a non-secure origin,
 *  which is how this app is opened on a real phone during development. */
export function canShareLink(): boolean {
  return typeof navigator !== 'undefined' && 'share' in navigator;
}

export function canCopyLink(): boolean {
  return typeof navigator !== 'undefined' && 'clipboard' in navigator;
}

/** What happened, so a glyph-only control can report it. `shared` needs no
 *  feedback — the OS drew a sheet over the app — and `copied` does, because
 *  nothing visible happened. */
export type ShareOutcome = 'shared' | 'copied' | 'unavailable';

/**
 * Share a URL, or copy it, or do neither and say so.
 *
 * THE SHARE SHEET WINS WHERE IT EXISTS, because that is the whole point on a phone:
 * a letter is passed on in whatever the two people already use, and the platform
 * knows what that is. Copy is the desktop answer.
 *
 * A DISMISSED SHEET IS A SHARE AND A REFUSED SHEET IS NOT, and telling those two
 * apart is the whole reason this is not one `catch`. Both reject:
 *
 *   - **`AbortError` means the person changed their mind.** Nothing to report and
 *     nothing to fall back to — flashing a failure at somebody who closed a sheet
 *     on purpose would be the control calling them wrong.
 *   - **Anything else means the platform would not do it** — a non-secure context,
 *     a WebView that advertises `share` and refuses it, a data shape it dislikes.
 *     That is not a share, so it falls through to the clipboard rather than
 *     reporting one. Without this the tap simply did nothing, visibly and
 *     silently, which is the worst of the three outcomes.
 */
export async function shareLink(url: string): Promise<ShareOutcome> {
  if (canShareLink()) {
    try {
      await navigator.share({ url });
      return 'shared';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return 'shared';
      /* Fall through and copy instead. */
    }
  }
  if (canCopyLink()) {
    try {
      await navigator.clipboard.writeText(url);
      return 'copied';
    } catch {
      /* A refused clipboard write is not worth an apology, but it must not
         report success — the control would say "Copied" over an empty
         clipboard. */
      return 'unavailable';
    }
  }
  return 'unavailable';
}
